import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { maskitoTransform, type MaskitoOptions } from '@maskito/core';
import { FormErrorMessage } from '@tryhackme/thm-ui-components';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useMaskito } from '@maskito/react';
import { maskitoWithPlaceholder } from '@maskito/kit';
import { MaskedFormInputProps } from './masked-form-input.types';
import { StyledIconGroupWrapper, StyledErrorIcon, StyledFormInput } from './masked-form-input.styles';
const MaskRules = {
  _: /\S/
};
export const MaskedFormInput = ({
  maskPlaceholder,
  errorMessage,
  placeholderChar = '_',
  onChange,
  onTypedValueChange,
  value,
  fixedCharIndexes = [],
  ...formInputProps
}: MaskedFormInputProps) => {
  const typedValueRef = useRef<string>('');
  const [valueWithPlaceholder, setValueWithPlaceholder] = useState<string>(maskPlaceholder);
  const maskOptions = useMemo(() => {
    const mask = [...maskPlaceholder].map((char: string, index) => {
      if (fixedCharIndexes.includes(index)) {
        return char;
      }
      return char === placeholderChar ? MaskRules[char] : char;
    });
    const {
      postprocessors,
      ...rest
    } = maskitoWithPlaceholder(maskPlaceholder);
    const options: MaskitoOptions = {
      mask,
      ...rest,
      postprocessors: [elementState => {
        typedValueRef.current = elementState.value;
        return elementState;
      }, ...postprocessors]
    };
    return options;
  }, [fixedCharIndexes, maskPlaceholder, placeholderChar]);
  useEffect(() => {
    if (maskPlaceholder.includes(placeholderChar)) {
      const transformedValue = maskitoTransform(value, maskOptions);
      setValueWithPlaceholder(transformedValue + maskPlaceholder.slice(transformedValue.length));
    }
  }, [maskOptions, maskPlaceholder, placeholderChar, value, valueWithPlaceholder]);
  const inputRef = useMaskito({
    options: maskOptions
  });
  return <>
      <StyledFormInput ref={inputRef} {...formInputProps} isError={!!errorMessage} placeholder="" rightAdornment={errorMessage ? <StyledIconGroupWrapper>
              <StyledErrorIcon icon={faCircleExclamation} title="Field is not valid" />
            </StyledIconGroupWrapper> : undefined} onInput={e => {
      onChange?.((e as ChangeEvent<HTMLInputElement>));
      setValueWithPlaceholder(e.currentTarget.value);
      onTypedValueChange?.(typedValueRef.current);
    }} value={valueWithPlaceholder} data-sentry-element="StyledFormInput" data-sentry-source-file="masked-form-input.tsx" />
      {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
    </>;
};